import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Globe,
  Plus,
  Search,
  Trash2,
  Zap,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import {
  formatDate,
  formatNumber,
  getStatusColor,
  getStrategyLabel,
  cn,
} from '@/lib/utils'
import toast from 'react-hot-toast'

export function DomainsPage() {
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  const { data, isLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.getDomains(),
  })

  const addMutation = useMutation({
    mutationFn: (domainName: string) => api.addDomain(domainName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      setShowAddModal(false)
      setNewDomain('')
      toast.success('Domain added successfully!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteDomain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast.success('Domain deleted')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const verifyMutation = useMutation({
    mutationFn: (id: string) => api.verifyDomain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast.success('Domain verified!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const filteredDomains = data?.domains?.filter((d) =>
    d.domain_name.toLowerCase().includes(search.toLowerCase())
  )

  const domainCount = data?.domains?.length || 0
  const domainLimit = user?.domainLimit || 2

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold">Domains</h1>
          <p className="text-muted-foreground">
            Manage your domain portfolio ({domainCount}/{domainLimit} used)
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={domainCount >= domainLimit}
          className="btn-primary"
        >
          <Plus className="h-4 w-4" />
          Add Domain
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search domains..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Domain List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : filteredDomains?.length === 0 ? (
        <div className="card text-center py-12">
          <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {search ? 'No domains found' : 'No domains yet'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {search
              ? 'Try a different search term'
              : 'Add your first domain to get started'}
          </p>
          {!search && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary"
            >
              <Plus className="h-4 w-4" />
              Add Domain
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDomains?.map((domain, i) => (
            <motion.div
              key={domain.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="card-hover"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Domain Info */}
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Globe className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/dashboard/domains/${domain.id}`}
                        className="font-semibold hover:text-primary transition-colors"
                      >
                        {domain.domain_name}
                      </Link>
                      <a
                        href={`https://${domain.domain_name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>Added {formatDate(domain.created_at)}</span>
                      {domain.monetization_strategy && (
                        <>
                          <span>•</span>
                          <span>{getStrategyLabel(domain.monetization_strategy)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <p className="font-semibold">{formatNumber(domain.total_visits)}</p>
                    <p className="text-muted-foreground">Visits</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">{formatNumber(domain.total_leads)}</p>
                    <p className="text-muted-foreground">Leads</p>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      'badge',
                      `badge-${getStatusColor(domain.verification_status)}`
                    )}
                  >
                    {domain.verification_status === 'verified' && (
                      <CheckCircle className="h-3 w-3 mr-1" />
                    )}
                    {domain.verification_status === 'failed' && (
                      <XCircle className="h-3 w-3 mr-1" />
                    )}
                    {domain.verification_status === 'pending' && (
                      <Clock className="h-3 w-3 mr-1" />
                    )}
                    {domain.verification_status}
                  </span>

                  <div className="flex items-center gap-1">
                    {/* Always show Generate button */}
                    <Link
                      to={`/dashboard/generate/${domain.id}`}
                      className="btn-ghost p-2"
                      title="Generate site"
                    >
                      <Zap className="h-4 w-4" />
                    </Link>

                    {domain.verification_status !== 'verified' && (
                      <button
                        onClick={() => verifyMutation.mutate(domain.id)}
                        disabled={verifyMutation.isPending}
                        className="btn-ghost p-2"
                        title="Verify domain (optional)"
                      >
                        {verifyMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this domain?')) {
                          deleteMutation.mutate(domain.id)
                        }
                      }}
                      className="btn-ghost p-2 text-destructive hover:bg-destructive/10"
                      title="Delete domain"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Domain Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md card"
            >
              <h2 className="text-xl font-semibold mb-4">Add Domain</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (newDomain.trim()) {
                    addMutation.mutate(newDomain.trim())
                  }
                }}
              >
                <div className="mb-4">
                  <label className="label mb-1.5 block">Domain Name</label>
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="example.com"
                    className="input"
                    autoFocus
                  />
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    Enter the domain without http:// or www.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addMutation.isPending || !newDomain.trim()}
                    className="btn-primary"
                  >
                    {addMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      'Add Domain'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Limit Warning */}
      {domainCount >= domainLimit && user?.subscriptionTier === 'free' && (
        <div className="card bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold">Domain limit reached</h3>
              <p className="text-sm text-muted-foreground">
                Upgrade to Pro for up to 10 domains
              </p>
            </div>
            <Link to="/dashboard/settings/billing" className="btn-primary btn-sm">
              Upgrade
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
